import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause, RotateCcw, Timer, Scale } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const BALANCE_POSES = [
  {
    id: 'one-leg-right',
    name: 'Balance on Right Leg',
    description: 'Stand tall on your right leg, left foot lifted',
    animation: 'oneLegRight'
  },
  {
    id: 'one-leg-left',
    name: 'Balance on Left Leg',
    description: 'Stand tall on your left leg, right foot lifted',
    animation: 'oneLegLeft'
  },
  {
    id: 'tree-pose-right',
    name: 'Tree Pose (Right)',
    description: 'Right leg standing, left foot on right knee',
    animation: 'treePoseRight'
  },
  {
    id: 'tree-pose-left',
    name: 'Tree Pose (Left)',
    description: 'Left leg standing, right foot on left knee',
    animation: 'treePoseLeft'
  },
  {
    id: 'airplane-right',
    name: 'Airplane (Right)',
    description: 'Balance on right leg, body and left leg horizontal',
    animation: 'airplaneRight'
  },
  {
    id: 'airplane-left',
    name: 'Airplane (Left)',
    description: 'Balance on left leg, body and right leg horizontal',
    animation: 'airplaneLeft'
  },
  {
    id: 'one-leg-one-hand',
    name: 'One Leg, One Hand',
    description: 'Balance on one leg and touch the ground with one hand',
    animation: 'oneLegOneHand'
  },
  {
    id: 'tiptoe-balance',
    name: 'Tiptoe Balance',
    description: 'Stand on your tiptoes with arms out',
    animation: 'tiptoeBalance'
  }
];

const DURATION_OPTIONS = [
  { label: '30 seconds', value: 30 },
  { label: '1 minute', value: 60 },
  { label: '2 minutes', value: 120 }
];

const POSE_DURATION = 10; // Each pose lasts 10 seconds

// Stick Figure Component with balance animations
const BalanceStickFigure = ({ animation, isActive }) => {
  const [wobble, setWobble] = useState(0);
  
  useEffect(() => {
    if (!isActive) return;
    const interval = setInterval(() => {
      setWobble(w => (w + 1) % 3);
    }, 800);
    return () => clearInterval(interval);
  }, [isActive]);

  // Slight wobble effect for balance
  const wobbleOffset = wobble === 1 ? 2 : wobble === 2 ? -2 : 0;

  const getStickFigure = () => {
    switch (animation) {
      case 'oneLegRight':
        return (
          <svg viewBox="0 0 100 150" className="w-full h-full">
            <g transform={`translate(${wobbleOffset}, 0)`}>
              <circle cx="50" cy="20" r="15" fill="none" stroke="currentColor" strokeWidth="3"/>
              <line x1="50" y1="35" x2="50" y2="80" stroke="currentColor" strokeWidth="3"/>
              <line x1="50" y1="50" x2="25" y2="60" stroke="currentColor" strokeWidth="3"/>
              <line x1="50" y1="50" x2="75" y2="60" stroke="currentColor" strokeWidth="3"/>
              {/* Standing leg */}
              <line x1="50" y1="80" x2="55" y2="120" stroke="currentColor" strokeWidth="3"/>
              {/* Lifted leg */}
              <line x1="50" y1="80" x2="35" y2="90" stroke="currentColor" strokeWidth="3"/>
              <line x1="35" y1="90" x2="30" y2="100" stroke="currentColor" strokeWidth="3"/>
            </g>
          </svg>
        );

      case 'oneLegLeft':
        return (
          <svg viewBox="0 0 100 150" className="w-full h-full">
            <g transform={`translate(${wobbleOffset}, 0)`}>
              <circle cx="50" cy="20" r="15" fill="none" stroke="currentColor" strokeWidth="3"/>
              <line x1="50" y1="35" x2="50" y2="80" stroke="currentColor" strokeWidth="3"/>
              <line x1="50" y1="50" x2="25" y2="60" stroke="currentColor" strokeWidth="3"/>
              <line x1="50" y1="50" x2="75" y2="60" stroke="currentColor" strokeWidth="3"/>
              {/* Standing leg */}
              <line x1="50" y1="80" x2="45" y2="120" stroke="currentColor" strokeWidth="3"/>
              {/* Lifted leg */}
              <line x1="50" y1="80" x2="65" y2="90" stroke="currentColor" strokeWidth="3"/>
              <line x1="65" y1="90" x2="70" y2="100" stroke="currentColor" strokeWidth="3"/>
            </g>
          </svg>
        );

      case 'treePoseRight':
        return (
          <svg viewBox="0 0 100 150" className="w-full h-full">
            <g transform={`translate(${wobbleOffset}, 0)`}>
              <circle cx="50" cy="20" r="15" fill="none" stroke="currentColor" strokeWidth="3"/>
              <line x1="50" y1="35" x2="50" y2="80" stroke="currentColor" strokeWidth="3"/>
              {/* Arms up like branches */}
              <line x1="50" y1="45" x2="30" y2="25" stroke="currentColor" strokeWidth="3"/>
              <line x1="50" y1="45" x2="70" y2="25" stroke="currentColor" strokeWidth="3"/>
              {/* Standing leg */}
              <line x1="50" y1="80" x2="55" y2="120" stroke="currentColor" strokeWidth="3"/>
              {/* Foot on knee */}
              <line x1="50" y1="80" x2="35" y2="85" stroke="currentColor" strokeWidth="3"/>
              <line x1="35" y1="85" x2="48" y2="95" stroke="currentColor" strokeWidth="3"/>
            </g>
          </svg>
        );

      case 'treePoseLeft':
        return (
          <svg viewBox="0 0 100 150" className="w-full h-full">
            <g transform={`translate(${wobbleOffset}, 0)`}>
              <circle cx="50" cy="20" r="15" fill="none" stroke="currentColor" strokeWidth="3"/>
              <line x1="50" y1="35" x2="50" y2="80" stroke="currentColor" strokeWidth="3"/>
              {/* Arms up like branches */}
              <line x1="50" y1="45" x2="30" y2="25" stroke="currentColor" strokeWidth="3"/>
              <line x1="50" y1="45" x2="70" y2="25" stroke="currentColor" strokeWidth="3"/>
              {/* Standing leg */}
              <line x1="50" y1="80" x2="45" y2="120" stroke="currentColor" strokeWidth="3"/>
              {/* Foot on knee */}
              <line x1="50" y1="80" x2="65" y2="85" stroke="currentColor" strokeWidth="3"/>
              <line x1="65" y1="85" x2="52" y2="95" stroke="currentColor" strokeWidth="3"/>
            </g>
          </svg>
        );

      case 'airplaneRight':
        return (
          <svg viewBox="0 0 120 120" className="w-full h-full">
            <g transform={`translate(${wobbleOffset}, 0)`}>
              <circle cx="30" cy="40" r="12" fill="none" stroke="currentColor" strokeWidth="3"/>
              {/* Body horizontal */}
              <line x1="42" y1="40" x2="90" y2="45" stroke="currentColor" strokeWidth="3"/>
              {/* Arms out */}
              <line x1="50" y1="40" x2="50" y2="20" stroke="currentColor" strokeWidth="3"/>
              <line x1="50" y1="40" x2="50" y2="60" stroke="currentColor" strokeWidth="3"/>
              {/* Standing leg */}
              <line x1="70" y1="45" x2="70" y2="95" stroke="currentColor" strokeWidth="3"/>
              {/* Back leg horizontal */}
              <line x1="90" y1="45" x2="110" y2="40" stroke="currentColor" strokeWidth="3"/>
            </g>
          </svg>
        );

      case 'airplaneLeft':
        return (
          <svg viewBox="0 0 120 120" className="w-full h-full">
            <g transform={`translate(${wobbleOffset}, 0)`}>
              <circle cx="90" cy="40" r="12" fill="none" stroke="currentColor" strokeWidth="3"/>
              {/* Body horizontal */}
              <line x1="78" y1="40" x2="30" y2="45" stroke="currentColor" strokeWidth="3"/>
              {/* Arms out */}
              <line x1="70" y1="40" x2="70" y2="20" stroke="currentColor" strokeWidth="3"/>
              <line x1="70" y1="40" x2="70" y2="60" stroke="currentColor" strokeWidth="3"/>
              {/* Standing leg */}
              <line x1="50" y1="45" x2="50" y2="95" stroke="currentColor" strokeWidth="3"/>
              {/* Back leg horizontal */}
              <line x1="30" y1="45" x2="10" y2="40" stroke="currentColor" strokeWidth="3"/>
            </g>
          </svg>
        );

      case 'oneLegOneHand':
        return (
          <svg viewBox="0 0 100 150" className="w-full h-full">
            <g transform={`translate(${wobbleOffset}, 0)`}>
              <circle cx="50" cy="30" r="15" fill="none" stroke="currentColor" strokeWidth="3"/>
              <line x1="50" y1="45" x2="50" y2="85" stroke="currentColor" strokeWidth="3"/>
              {/* One arm reaching down */}
              <line x1="50" y1="55" x2="30" y2="90" stroke="currentColor" strokeWidth="3"/>
              {/* Other arm out for balance */}
              <line x1="50" y1="55" x2="80" y2="45" stroke="currentColor" strokeWidth="3"/>
              {/* Standing leg */}
              <line x1="50" y1="85" x2="55" y2="125" stroke="currentColor" strokeWidth="3"/>
              {/* Lifted leg back */}
              <line x1="50" y1="85" x2="75" y2="80" stroke="currentColor" strokeWidth="3"/>
              {/* Hand on ground indicator */}
              <circle cx="30" cy="95" r="5" fill="currentColor"/>
            </g>
          </svg>
        );

      case 'tiptoeBalance':
        return (
          <svg viewBox="0 0 100 150" className="w-full h-full">
            <g transform={`translate(${wobbleOffset}, 0)`}>
              <circle cx="50" cy="15" r="15" fill="none" stroke="currentColor" strokeWidth="3"/>
              <line x1="50" y1="30" x2="50" y2="75" stroke="currentColor" strokeWidth="3"/>
              {/* Arms out for balance */}
              <line x1="50" y1="45" x2="20" y2="45" stroke="currentColor" strokeWidth="3"/>
              <line x1="50" y1="45" x2="80" y2="45" stroke="currentColor" strokeWidth="3"/>
              {/* Legs together */}
              <line x1="50" y1="75" x2="45" y2="110" stroke="currentColor" strokeWidth="3"/>
              <line x1="50" y1="75" x2="55" y2="110" stroke="currentColor" strokeWidth="3"/>
              {/* On tiptoes - small feet */}
              <line x1="45" y1="110" x2="45" y2="118" stroke="currentColor" strokeWidth="4"/>
              <line x1="55" y1="110" x2="55" y2="118" stroke="currentColor" strokeWidth="4"/>
            </g>
          </svg>
        );

      default:
        return (
          <svg viewBox="0 0 100 150" className="w-full h-full">
            <circle cx="50" cy="20" r="15" fill="none" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="35" x2="50" y2="80" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="50" x2="30" y2="70" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="50" x2="70" y2="70" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="80" x2="35" y2="120" stroke="currentColor" strokeWidth="3"/>
            <line x1="50" y1="80" x2="65" y2="120" stroke="currentColor" strokeWidth="3"/>
          </svg>
        );
    }
  };

  return (
    <div className="w-48 h-48 text-blue-600">
      {getStickFigure()}
    </div>
  );
};

const BrilliantBalancer = () => {
  const navigate = useNavigate();
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentPoseIndex, setCurrentPoseIndex] = useState(0);
  const [poseTimer, setPoseTimer] = useState(POSE_DURATION);
  const [totalTimeRemaining, setTotalTimeRemaining] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const intervalRef = useRef(null);

  const currentPose = BALANCE_POSES[currentPoseIndex % BALANCE_POSES.length];

  useEffect(() => {
    if (isRunning && !isPaused) {
      intervalRef.current = setInterval(() => {
        setPoseTimer(prev => {
          if (prev <= 1) {
            // Move to next pose
            setCurrentPoseIndex(i => i + 1);
            return POSE_DURATION;
          }
          return prev - 1;
        });

        setTotalTimeRemaining(prev => {
          if (prev <= 1) {
            setIsRunning(false);
            setIsComplete(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, isPaused]);

  const startSession = (duration) => {
    setSelectedDuration(duration);
    setTotalTimeRemaining(duration);
    setPoseTimer(POSE_DURATION);
    setCurrentPoseIndex(0);
    setIsRunning(true);
    setIsPaused(false);
    setIsComplete(false);
  };

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  const resetSession = () => {
    setIsRunning(false);
    setIsPaused(false);
    setSelectedDuration(null);
    setCurrentPoseIndex(0);
    setPoseTimer(POSE_DURATION);
    setTotalTimeRemaining(0);
    setIsComplete(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E3F2FD] via-[#BBDEFB] to-[#90CAF9] relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-indigo-400 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-8">
        <header className="flex justify-between items-center mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-white text-[#2D3748] px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 flex items-center gap-2 shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748] flex items-center gap-3">
            <Scale className="w-8 h-8" />
            Brilliant Balancer
          </h1>
          
          <button
            onClick={resetSession}
            className="bg-white text-[#4A5568] px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 flex items-center gap-2 shadow-sm"
          >
            <RotateCcw className="w-5 h-5" />
            Reset
          </button>
        </header>

        {!isRunning && !isComplete && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 mb-8">
              <p className="text-lg text-[#4A5568]">
                Practice balance poses! Each pose lasts 10 seconds. Find your center and stay steady!
              </p>
            </div>

            <h2 className="text-2xl font-bold text-[#2D3748] mb-6">Choose Your Session Duration</h2>
            
            <div className="flex flex-wrap justify-center gap-4">
              {DURATION_OPTIONS.map(option => (
                <button
                  key={option.value}
                  onClick={() => startSession(option.value)}
                  className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:-translate-y-1 transition-all duration-300 shadow-lg flex items-center gap-3"
                >
                  <Timer className="w-6 h-6" />
                  {option.label}
                </button>
              ))}
            </div>

            {/* Preview poses */}
            <div className="mt-12">
              <h3 className="text-xl font-semibold text-[#2D3748] mb-4">Balance Poses Include:</h3>
              <div className="flex flex-wrap justify-center gap-3">
                {BALANCE_POSES.map(pose => (
                  <span key={pose.id} className="bg-white/80 px-4 py-2 rounded-full text-[#4A5568] text-sm">
                    {pose.name}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {isRunning && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            {/* Total time remaining */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-8 py-4 inline-block mb-6">
              <p className="text-sm text-[#4A5568]">Total Time Remaining</p>
              <p className="text-3xl font-bold text-[#2D3748]">{formatTime(totalTimeRemaining)}</p>
            </div>

            {/* Current pose */}
            <div className="bg-white rounded-3xl p-8 shadow-xl mb-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentPose.id}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                >
                  <h2 className="text-3xl font-bold text-[#2D3748] mb-2">{currentPose.name}</h2>
                  <p className="text-lg text-[#4A5568] mb-6">{currentPose.description}</p>
                  
                  {/* Stick figure animation */}
                  <div className="flex justify-center mb-6">
                    <BalanceStickFigure animation={currentPose.animation} isActive={!isPaused} />
                  </div>

                  {/* Pose timer */}
                  <div className="relative w-32 h-32 mx-auto mb-4">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        fill="none"
                        stroke="#E5E7EB"
                        strokeWidth="8"
                      />
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        fill="none"
                        stroke="#3B82F6"
                        strokeWidth="8"
                        strokeLinecap="round"
                        strokeDasharray={2 * Math.PI * 56}
                        strokeDashoffset={2 * Math.PI * 56 * (1 - poseTimer / POSE_DURATION)}
                        className="transition-all duration-300"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-4xl font-bold text-[#2D3748]">{poseTimer}</span>
                    </div>
                  </div>

                  {/* Balance tip */}
                  <div className="bg-blue-50 rounded-xl px-4 py-2 inline-block">
                    <p className="text-sm text-blue-700">💡 Focus on a fixed point to help maintain balance</p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Controls */}
            <div className="flex justify-center gap-4">
              <button
                onClick={togglePause}
                className={`px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 shadow-lg flex items-center gap-2 ${
                  isPaused 
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' 
                    : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                }`}
              >
                {isPaused ? <Play className="w-6 h-6" /> : <Pause className="w-6 h-6" />}
                {isPaused ? 'Resume' : 'Pause'}
              </button>
            </div>

            {/* Pose queue */}
            {(() => {
              const totalPosesInSession = Math.ceil(selectedDuration / POSE_DURATION);
              const upcomingOffsets = [1, 2, 3].filter(
                offset => currentPoseIndex + offset < totalPosesInSession
              );
              if (upcomingOffsets.length === 0) return null;
              return (
                <div className="mt-8 bg-white/60 backdrop-blur-sm rounded-2xl p-4">
                  <p className="text-sm text-[#4A5568] mb-2">Coming up next:</p>
                  <div className="flex justify-center gap-2 flex-wrap">
                    {upcomingOffsets.map(offset => {
                      const nextPose = BALANCE_POSES[(currentPoseIndex + offset) % BALANCE_POSES.length];
                      return (
                        <span key={offset} className="bg-white px-3 py-1 rounded-full text-sm text-[#4A5568]">
                          {nextPose.name}
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </motion.div>
        )}

        {isComplete && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className="bg-white rounded-3xl p-12 shadow-xl">
              <div className="text-6xl mb-4">🧘</div>
              <h2 className="text-3xl font-bold text-[#2D3748] mb-2">Session Complete!</h2>
              <p className="text-lg text-[#4A5568] mb-2">
                Wonderful balance work! You completed {formatTime(selectedDuration)}!
              </p>
              <p className="text-[#4A5568] mb-8">
                You practiced {Math.ceil(selectedDuration / POSE_DURATION)} balance poses!
              </p>
              
              <button
                onClick={resetSession}
                className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:-translate-y-1 transition-all duration-300 shadow-lg"
              >
                Start Another Session
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BrilliantBalancer;
